// Starlight: delivery date range for dropshipped items, with an honest order-by countdown.
// Attributes:
//   data-min / data-max  delivery days
//   data-business        count business days only (weekends skipped)
//   data-tz              IANA timezone used for "today" and midnight (default: shopper's browser)
// The "Order within X hrs Y mins" line only shows when ordering after midnight
// would actually change the delivery dates (so no countdown on Fridays and Saturdays).
if (!customElements.get('sl-delivery-countdown')) {
  customElements.define(
    'sl-delivery-countdown',
    class SlDeliveryCountdown extends HTMLElement {
      connectedCallback() {
        this.min = parseInt(this.dataset.min, 10);
        this.max = parseInt(this.dataset.max, 10);
        this.business = this.hasAttribute('data-business');
        this.tz = this.dataset.tz || undefined;
        this.rangeEl = this.querySelector('[data-range]');
        this.countdownWrap = this.querySelector('[data-countdown-wrap]');
        this.countdownTime = this.querySelector('[data-countdown-time]');
        if (!this.rangeEl || Number.isNaN(this.min) || Number.isNaN(this.max)) return;

        this.format = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });
        this.render();
        if (this.countdownWrap) this.timer = setInterval(() => this.render(), 30000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      // Current date and time in the chosen timezone.
      now() {
        let parts;
        try {
          parts = new Intl.DateTimeFormat('en-US', {
            timeZone: this.tz,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hourCycle: 'h23',
          }).formatToParts(new Date());
        } catch (e) {
          this.tz = undefined;
          return this.now();
        }
        const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
        return {
          date: new Date(Date.UTC(get('year'), get('month') - 1, get('day'))),
          seconds: get('hour') * 3600 + get('minute') * 60 + get('second'),
        };
      }

      addDays(start, days) {
        const date = new Date(start.getTime());
        if (!this.business) {
          date.setUTCDate(date.getUTCDate() + days);
          return date;
        }
        let added = 0;
        while (added < days) {
          date.setUTCDate(date.getUTCDate() + 1);
          const day = date.getUTCDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return date;
      }

      rangeFrom(start) {
        const first = this.format.format(this.addDays(start, this.min));
        const last = this.format.format(this.addDays(start, this.max));
        return this.min === this.max ? last : `${first} – ${last}`;
      }

      render() {
        const { date, seconds } = this.now();
        const today = this.rangeFrom(date);
        this.rangeEl.textContent = today;
        if (!this.countdownWrap) return;

        const tomorrow = new Date(date.getTime());
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        const changesAtMidnight = this.rangeFrom(tomorrow) !== today;
        this.countdownWrap.hidden = !changesAtMidnight;
        if (!changesAtMidnight || !this.countdownTime) return;

        const minutesLeft = Math.max(1, Math.ceil((86400 - seconds) / 60));
        const hours = Math.floor(minutesLeft / 60);
        const minutes = minutesLeft % 60;
        const hrText = hours ? `${hours} hr${hours === 1 ? '' : 's'}` : '';
        const minText = minutes ? `${minutes} min${minutes === 1 ? '' : 's'}` : '';
        this.countdownTime.textContent = [hrText, minText].filter(Boolean).join(' ');
      }
    }
  );
}
